import React, { useContext } from "react";
import { InlineMath } from "react-katex";
import { ThemeContext } from "../../context/ThemeContext";

/* ------------------ pomocnicze uproszczenie pierwiastków ------------------ */
function simplifySqrt(n) {
  let factor = 1;
  for (let i = Math.floor(Math.sqrt(n)); i >= 2; i--) {
    if (n % (i * i) === 0) {
      factor = i;
      break;
    }
  }
  const outside = factor;
  const inside = n / (factor * factor);

  if (inside === 1) return `${outside}`;
  if (outside === 1) return `\\sqrt{${inside}}`;
  return `${outside}\\sqrt{${inside}}`;
}

/* ------------------------------ KOMPONENT ---------------------------------- */

export default function TriangleDiagram({
  a,
  b,
  c,
  angleAt = "B",
  labelSides = {},
  labelA = null,
  labelB = null,
  labelC = null,
  showSideLabels = true,
  showBetaAngle = false,
  // NOWE – pozwalają podmienić napis przy kątach
  alphaLabel = "\\alpha",
  betaLabel = "\\beta",
  mirrored = false,
  vertexLabels = {},
}) {
  /* ---- etykiety przekazane z zewnątrz / domyślne ---- */

  labelA = labelA ?? labelSides.a ?? null;
  labelB = labelB ?? labelSides.b ?? null;
  labelC = labelC ?? labelSides.c ?? null;

  /* ---- długości boków używane tylko do proporcji rysunku ---- */

  let sideA = typeof a === "number" ? a : null;
  let sideB = typeof b === "number" ? b : null;
  let sideC = typeof c === "number" ? c : null;

  // jeśli któryś bok nie jest podany, próbujemy go wyliczyć
  if (sideA == null && sideB != null && sideC != null)
    sideA = Math.sqrt(Math.max(sideC * sideC - sideB * sideB, 1));

  if (sideB == null && sideA != null && sideC != null)
    sideB = Math.sqrt(Math.max(sideC * sideC - sideA * sideA, 1));

  if (sideC == null && sideA != null && sideB != null)
    sideC = Math.sqrt(sideA * sideA + sideB * sideB);

  // fallbacki – żeby zawsze coś się narysowało
  if (sideA == null) sideA = 3;
  if (sideB == null) sideB = 4;
  if (sideC == null) sideC = Math.sqrt(sideA * sideA + sideB * sideB);

  const maxSide = Math.max(sideA, sideB, sideC);
  const scale = 160 / maxSide;
  const padding = 50;

  /* ---- współrzędne ---- */

  const width = sideA * scale + 2 * padding;
  const height = sideB * scale + 2 * padding;

  const ax = padding;
  const ay = height - padding;

  const bx = ax + sideA * scale;
  const by = ay;

  const cx = ax;
  const cy = ay - sideB * scale;

  const { theme } = useContext(ThemeContext);
  const textColor = theme === "light" ? "#000" : "#fff";

  const cSquared = sideA * sideA + sideB * sideB;
  const cLatex = simplifySqrt(cSquared);

  const labelForA =
    labelA != null ? labelA : typeof a === "number" ? String(a) : String(sideA);
  const labelForB =
    labelB != null ? labelB : typeof b === "number" ? String(b) : String(sideB);
  const labelForC =
    labelC != null ? labelC : typeof c === "number" ? String(c) : cLatex;

  /*
   * Podpis przeciwprostokątnej odsuwamy wzdłuż jej zewnętrznej normalnej.
   * Dzięki temu jego środek pozostaje w tym samym miejscu także po odbiciu
   * całego SVG, niezależnie od długości napisu (litera albo liczba).
   */
  const hypotenuseLength = Math.hypot(bx - cx, by - cy);
  const hypotenuseLabelOffset = 18;
  const hypotenuseLabelX =
    (bx + cx) / 2 + ((by - cy) / hypotenuseLength) * hypotenuseLabelOffset;
  const hypotenuseLabelY =
    (by + cy) / 2 - ((bx - cx) / hypotenuseLength) * hypotenuseLabelOffset;

  /* ------------------------------ RENDER ----------------------------------- */

  return (
    <svg
      width={width}
      height={height}
      style={{
        display: "block",
        margin: "0 auto",
        transform: mirrored ? "scaleX(-1)" : undefined,
      }}
    >
      {/* trójkąt */}
      <polygon
        points={`${ax},${ay} ${bx},${by} ${cx},${cy}`}
        fill={theme === "light" ? "#f9f9f9" : "#000"}
        stroke={textColor}
        strokeWidth="2"
      />

      {/* wierzchołki */}
      <foreignObject x={ax - 25} y={ay + 5} width="20" height="20">
        <div style={{ color: textColor, fontSize: "14px", transform: mirrored ? "scaleX(-1)" : undefined }}>
          <InlineMath math={vertexLabels.A ?? "A"} />
        </div>
      </foreignObject>

      <foreignObject x={bx + 5} y={by + 5} width="20" height="20">
        <div style={{ color: textColor, fontSize: "14px", transform: mirrored ? "scaleX(-1)" : undefined }}>
          <InlineMath math={vertexLabels.B ?? "B"} />
        </div>
      </foreignObject>

      <foreignObject x={cx - 25} y={cy - 20} width="20" height="20">
        <div style={{ color: textColor, fontSize: "14px", transform: mirrored ? "scaleX(-1)" : undefined }}>
          <InlineMath math={vertexLabels.C ?? "C"} />
        </div>
      </foreignObject>

      {/* ----------------------------------- PODPISY BOKÓW ----------------------------------- */}

      {showSideLabels && (
        <>
          {/* a – poziomy */}
          <foreignObject
            x={(ax + bx) / 2 - 25}
            y={ay + 8}
            width="50"
            height="24"
          >
            <div style={{ color: textColor, textAlign: "center", transform: mirrored ? "scaleX(-1)" : undefined }}>
              <InlineMath math={labelForA} />
            </div>
          </foreignObject>

          {/* b – pionowy */}
          <foreignObject
            x={ax - 38}
            y={(ay + cy) / 2 - 12}
            width="32"
            height="24"
          >
            <div style={{ color: textColor, textAlign: "center", transform: mirrored ? "scaleX(-1)" : undefined }}>
              <InlineMath math={labelForB} />
            </div>
          </foreignObject>

          {/* c – przeciwprostokątna */}
          <foreignObject
            x={hypotenuseLabelX - 30}
            y={hypotenuseLabelY - 12}
            width="60"
            height="24"
          >
            <div style={{ color: textColor, textAlign: "center", transform: mirrored ? "scaleX(-1)" : undefined }}>
              <InlineMath math={labelForC} />
            </div>
          </foreignObject>
        </>
      )}

      {/* ----------------------------------- KĄT α ----------------------------------- */}

      {angleAt === "B" &&
        (() => {
          const angle = Math.atan(sideB / sideA);
          const radius = Math.min(44, Math.max(28, 9 / Math.sin(angle / 2)));
          const centerX = bx - radius * Math.cos(angle / 2);
          const centerY = by - radius * Math.sin(angle / 2);

          return (
            <foreignObject x={centerX - 14} y={centerY - 14} width="28" height="28">
              <div
                style={{
                  alignItems: "center",
                  color: textColor,
                  display: "flex",
                  fontSize: "14px",
                  height: "100%",
                  justifyContent: "center",
                  transform: mirrored ? "scaleX(-1)" : undefined,
                  width: "100%",
                }}
              >
                <InlineMath math={alphaLabel} />
              </div>
            </foreignObject>
          );
        })()}

      {angleAt === "C" &&
        (() => {
          const angle = Math.atan(sideA / sideB);
          const hypotenuseDirection = Math.atan(sideB / sideA);
          const bisectorDirection = (hypotenuseDirection + Math.PI / 2) / 2;
          const radius = Math.min(44, Math.max(28, 9 / Math.sin(angle / 2)));
          const centerX = cx + radius * Math.cos(bisectorDirection);
          const centerY = cy + radius * Math.sin(bisectorDirection);

          return (
            <foreignObject x={centerX - 14} y={centerY - 14} width="28" height="28">
              <div
                style={{
                  alignItems: "center",
                  color: textColor,
                  display: "flex",
                  fontSize: "14px",
                  height: "100%",
                  justifyContent: "center",
                  transform: mirrored ? "scaleX(-1)" : undefined,
                  width: "100%",
                }}
              >
                <InlineMath math={alphaLabel} />
              </div>
            </foreignObject>
          );
        })()}

      {/* ----------------------------------- KĄT β ----------------------------------- */}

      {showBetaAngle &&
        angleAt === "B" &&
        (() => {
          const angleDeg = (Math.atan(sideA / sideB) * 180) / Math.PI;

          let offsetY =
            angleDeg < 20 ? +65 : angleDeg < 40 ? +40 : angleDeg < 60 ? +15 : +8;

          return (
            <foreignObject x={cx + 5} y={cy + offsetY} width="20" height="20">
              <div style={{ color: textColor, fontSize: "14px", transform: mirrored ? "scaleX(-1)" : undefined }}>
                <InlineMath math={betaLabel} />
              </div>
            </foreignObject>
          );
        })()}

      {showBetaAngle &&
        angleAt === "C" &&
        (() => {
          const angleDeg = (Math.atan(sideB / sideA) * 180) / Math.PI;

          let offsetX =
            angleDeg < 20 ? -70 : angleDeg < 40 ? -50 : angleDeg < 60 ? -35 : -25;

          return (
            <foreignObject x={bx + offsetX} y={by - 21} width="20" height="20">
              <div style={{ color: textColor, fontSize: "14px", transform: mirrored ? "scaleX(-1)" : undefined }}>
                <InlineMath math={betaLabel} />
              </div>
            </foreignObject>
          );
        })()}

      {/* ----------------------------------- KĄT PROSTY ----------------------------------- */}

      <path
        d={`M ${ax + 15} ${ay} A 15 15 0 0 0 ${ax} ${ay - 15}`}
        fill="none"
        stroke={textColor}
        strokeWidth="2"
      />
      <circle cx={ax + 6} cy={ay - 6} r="2" fill={textColor} />
    </svg>
  );
}
